import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import HabboImage from '../components/HabboImage';
import ColorPicker from '../components/ColorPicker';
import { useProfileSkins, isSkinSaveable } from '../data/profileSkins';

import { renderSceneToCanvas } from '../utils/sceneRenderer';
import { getBadgeUrl } from '../utils/badgeUrl';
import {
    getUserMessages,
    markMessageAsRead,
    deleteMessage,
    respondProfileFriendRequest,
    getProfileData,
    upsertOwnProfileData,
    getProfilePageLoad,
    getImagerBlob,
} from '../services/api';

// ── Utilities (mirrors Profile.jsx) ───────────────────────────────────────────
const sanitiseHex = (v) => (/^#[0-9a-fA-F]{6}$/.test(String(v || '')) ? v : null);

const base64ToUint8 = (b64) => {
    const bin = atob(b64);
    const u8 = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
    return u8;
};
const binarySafeB64ToUtf8 = (b64) => decodeURIComponent(escape(atob(b64)));
const gunzipBase64ToString = async (b64) => {
    try {
        if (typeof DecompressionStream === 'undefined') return binarySafeB64ToUtf8(b64);
        const ds = new DecompressionStream('gzip');
        const stream = new Blob([base64ToUint8(b64)]).stream().pipeThrough(ds);
        return await new Response(stream).text();
    } catch {
        return binarySafeB64ToUtf8(b64);
    }
};

const speechPartsBase = {
    frnt: `${process.env.PUBLIC_URL}/img/spbb_frnt.png`,
    mid:  `${process.env.PUBLIC_URL}/img/spbb_mid.png`,
    end:  `${process.env.PUBLIC_URL}/img/spbb_end.png`,
};

// Assets that belong on the box (header or body placement)
const getBoxAssets = (skin) => (skin?.assets || []).filter(a => {
    const p = a.placement;
    if (!p) return true;
    if (Array.isArray(p)) return p.some(pl => pl === 'header' || pl === 'body');
    return p === 'header' || p === 'body';
});

// ── Retro scrollbar styles ─────────────────────────────────────────────────────
const retroScrollbarStyles = `
  .scrollbar-retro::-webkit-scrollbar { width: 16px; }
  .scrollbar-retro::-webkit-scrollbar-track { background: #dfdfdf; box-shadow: inset 1px 1px #808080, inset -1px -1px #ffffff; }
  .scrollbar-retro::-webkit-scrollbar-thumb { background: #c0c0c0; border: 2px solid; border-color: #ffffff #808080 #808080 #ffffff; }
  .scrollbar-retro::-webkit-scrollbar-button { background: #c0c0c0; border: 1px solid; border-color: #ffffff #808080 #808080 #ffffff; display: block; height: 16px; }
  .scrollbar-retro::-webkit-scrollbar-button:start:increment { display: none; }
  .scrollbar-retro::-webkit-scrollbar-button:end:decrement { display: none; }
`;

function Messages({ userData }) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [activeView, setActiveView] = useState('inbox');
    const [responding, setResponding] = useState(false);
    const [checkedIds, setCheckedIds] = useState(new Set());

    // ── Saved inbox settings ───────────────────────────────────────────────────
    const [inboxSettings, setInboxSettings] = useState({ pageBg: null, headerBg: null, skinId: 'default' });

    // ── Settings modal draft state ─────────────────────────────────────────────
    const [showInboxSettingsModal, setShowInboxSettingsModal] = useState(false);
    const [draftPageBg, setDraftPageBg] = useState(null);
    const [draftHeaderBg, setDraftHeaderBg] = useState(null);
    const [draftSkinId, setDraftSkinId] = useState('default');
    const [ownedSkinIds, setOwnedSkinIds] = useState([]);

    // ── Page background sub-modal (mirrors Profile.jsx bg modal) ──────────────
    const [showPageBgModal, setShowPageBgModal] = useState(false);
    const [bgModalMode, setBgModalMode] = useState('color');
    const [bgPickerDraftColor, setBgPickerDraftColor] = useState('#ffffff');
    const [tiledScenes, setTiledScenes] = useState([]);
    const [isFetchingTiledScenes, setIsFetchingTiledScenes] = useState(false);
    const [selectedBgSceneId, setSelectedBgSceneId] = useState(null);
    const [tiledScenePreviews, setTiledScenePreviews] = useState(new Map());
    const tiledSceneCanvasesRef = useRef(new Map());

    // ── Skin picker popover ────────────────────────────────────────────────────
    const [skinPickerOpen, setSkinPickerOpen] = useState(false);
    const [skinPickerPos, setSkinPickerPos] = useState({ top: 0, right: 0 });
    const skinBtnRef = useRef(null);
    const skinPickerRef = useRef(null);

    const isStaff = Boolean(userData?.hasStaffRole);
    const allSkins = useProfileSkins();
    const SKINS = useMemo(
        () => allSkins.filter(s => !s.staffOnly || isStaff),
        [allSkins, isStaff]
    );

    // ── Effective display values: draft while modal open, saved otherwise ──────
    const displaySkinId  = showInboxSettingsModal ? draftSkinId  : inboxSettings.skinId;
    const displayHeaderBg = showInboxSettingsModal ? draftHeaderBg : inboxSettings.headerBg;
    const displayPageBg   = showInboxSettingsModal ? draftPageBg   : inboxSettings.pageBg;

    const displaySkin = useMemo(
        () => SKINS.find(s => s.id === displaySkinId && s.id !== 'default') ?? null,
        [SKINS, displaySkinId]
    );

    // ── Load on mount + live reload on new incoming message ───────────────────
    useEffect(() => { loadMessages(); }, []);

    useEffect(() => {
        const handler = () => loadMessages();
        window.addEventListener('frankly:newMessage', handler);
        return () => window.removeEventListener('frankly:newMessage', handler);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (userData) {
            loadInboxSettings();
            loadOwnedSkins();
        }
    }, [userData]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Skin picker click-outside ──────────────────────────────────────────────
    useEffect(() => {
        if (!skinPickerOpen) return;
        const handler = (e) => {
            if (!skinPickerRef.current?.contains(e.target) && !skinBtnRef.current?.contains(e.target))
                setSkinPickerOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [skinPickerOpen]);

    // ── Tiled scene preview generation (mirrors Profile.jsx) ──────────────────
    const generateTiledSceneCanvas = useCallback(async (scene) => {
        if (!scene?.id) return;
        if (tiledSceneCanvasesRef.current.has(scene.id)) return;
        tiledSceneCanvasesRef.current.set(scene.id, null);
        try {
            const canvas = await renderSceneToCanvas(scene, {
                proxyUrl: (u) => u,
                publicUrl: process.env.PUBLIC_URL,
                speechParts: speechPartsBase,
            });
            tiledSceneCanvasesRef.current.set(scene.id, canvas);
            await new Promise(resolve => {
                canvas.toBlob(blob => {
                    if (blob) setTiledScenePreviews(prev => new Map(prev).set(scene.id, URL.createObjectURL(blob)));
                    resolve();
                }, 'image/png');
            });
        } catch {
            tiledSceneCanvasesRef.current.delete(scene.id);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!showPageBgModal || !tiledScenes.length) return;
        const firstId = tiledScenes[0].id;
        setSelectedBgSceneId(prev => (prev && tiledScenes.find(s => s.id === prev)) ? prev : firstId);
        let cancelled = false;
        (async () => { for (const scene of tiledScenes) { if (cancelled) break; await generateTiledSceneCanvas(scene); } })();
        return () => { cancelled = true; };
    }, [tiledScenes, showPageBgModal, generateTiledSceneCanvas]);

    useEffect(() => {
        if (!selectedBgSceneId || !showPageBgModal) return;
        const scene = tiledScenes.find(s => s.id === selectedBgSceneId);
        if (scene) generateTiledSceneCanvas(scene);
    }, [selectedBgSceneId, showPageBgModal, tiledScenes, generateTiledSceneCanvas]);

    // ── Data loaders ───────────────────────────────────────────────────────────
    const loadMessages = async () => {
        try {
            setLoading(true);
            const response = await getUserMessages(true);
            if (response.success) setMessages(response.messages);
            else toast.error('Failed to load messages');
        } catch { toast.error('Failed to load messages'); }
        finally { setLoading(false); }
    };

    const loadInboxSettings = async () => {
        try {
            const username = userData?.username || userData?.habboName;
            if (!username) return;
            const r = await getProfileData(username, 'inbox_settings');
            if (r?.success && r?.data?.jsonPayload) {
                const p = typeof r.data.jsonPayload === 'string' ? JSON.parse(r.data.jsonPayload) : r.data.jsonPayload;
                setInboxSettings({ pageBg: p.pageBg || null, headerBg: p.headerBg || null, skinId: p.skinId || 'default' });
            }
        } catch {}
    };

    const loadOwnedSkins = async () => {
        try {
            const username = userData?.username || userData?.habboName;
            if (!username) return;
            const data = await getProfilePageLoad(username);
            if (Array.isArray(data?.ownedSkins)) setOwnedSkinIds(data.ownedSkins);
        } catch {}
    };

    // ── Skin picker ────────────────────────────────────────────────────────────
    const openSkinPicker = (e) => {
        e.stopPropagation();
        const rect = skinBtnRef.current.getBoundingClientRect();
        const PICKER_HEIGHT = 230;
        const right = window.innerWidth - rect.right;
        setSkinPickerPos(
            window.innerHeight - rect.bottom < PICKER_HEIGHT && rect.top > window.innerHeight - rect.bottom
                ? { bottom: window.innerHeight - rect.top + 2, top: undefined, right }
                : { top: rect.bottom + 2, bottom: undefined, right }
        );
        setSkinPickerOpen(v => !v);
    };

    // ── Settings modal ─────────────────────────────────────────────────────────
    const handleOpenInboxSettings = () => {
        setDraftPageBg(inboxSettings.pageBg);
        setDraftHeaderBg(inboxSettings.headerBg);
        setDraftSkinId(inboxSettings.skinId || 'default');
        setSkinPickerOpen(false);
        setShowInboxSettingsModal(true);
    };

    const handleSaveInboxSettings = async () => {
        const skinId = draftSkinId || 'default';
        if (!isSkinSaveable(skinId, isStaff, ownedSkinIds)) {
            toast.error("You don't own that skin!");
            return;
        }
        const pageBgValue = sanitiseHex(draftPageBg) ?? (typeof draftPageBg === 'string' && (draftPageBg.startsWith('data:') || draftPageBg.startsWith('http')) ? draftPageBg : null);
        const payload = { pageBg: pageBgValue, headerBg: sanitiseHex(draftHeaderBg) || null, skinId };
        try {
            const r = await upsertOwnProfileData('inbox_settings', payload);
            if (r?.success) { setInboxSettings(payload); toast.success('Inbox settings saved!'); setShowInboxSettingsModal(false); }
            else toast.error('Failed to save inbox settings');
        } catch { toast.error('Failed to save inbox settings'); }
    };

    const handleResetInboxSettings = async () => {
        const defaultPayload = { pageBg: null, headerBg: null, skinId: 'default' };
        try {
            const r = await upsertOwnProfileData('inbox_settings', defaultPayload);
            if (r?.success) { setInboxSettings(defaultPayload); toast.success('Inbox reset to default'); setShowInboxSettingsModal(false); }
            else toast.error('Failed to reset inbox settings');
        } catch { toast.error('Failed to reset inbox settings'); }
    };

    const handleCancelSettings = () => {
        setShowInboxSettingsModal(false);
    };

    // ── Page background sub-modal ──────────────────────────────────────────────
    const handleOpenPageBgModal = async () => {
        setBgModalMode(tiledScenes.length > 0 || selectedBgSceneId ? (selectedBgSceneId ? 'scene' : 'color') : 'color');
        setBgPickerDraftColor(sanitiseHex(draftPageBg) || '#ffffff');
        setShowPageBgModal(true);
        if (tiledScenes.length > 0) return;
        setIsFetchingTiledScenes(true);
        try {
            const r = await getImagerBlob();
            if (r?.success && r?.data?.blob) {
                const json = await gunzipBase64ToString(r.data.blob);
                const payload = JSON.parse(json);
                setTiledScenes((Array.isArray(payload.scenes) ? payload.scenes : []).filter(s => s.tileMode));
            }
        } catch { toast.error('Failed to load tiled scenes.'); }
        finally { setIsFetchingTiledScenes(false); }
    };

    const handleConfirmPageBgModal = async () => {
        if (bgModalMode === 'color') {
            setDraftPageBg(bgPickerDraftColor);
            setShowPageBgModal(false);
            return;
        }
        const canvas = tiledSceneCanvasesRef.current.get(selectedBgSceneId);
        if (!canvas) { toast.error('Scene preview not ready yet.'); return; }
        try {
            const dataUrl = await new Promise((resolve, reject) => {
                canvas.toBlob(blob => {
                    if (!blob) { resolve(canvas.toDataURL('image/png')); return; }
                    const r = new FileReader();
                    r.onload = () => resolve(r.result);
                    r.onerror = reject;
                    r.readAsDataURL(blob);
                }, 'image/png');
            });
            setDraftPageBg(dataUrl);
            setShowPageBgModal(false);
        } catch { toast.error('Failed to compile background.'); }
    };

    // ── Skin asset rendering (mirrors EditableBox.renderSkinAsset) ─────────────
    const renderSkinAsset = (asset, i) => {
        const { image, anchor, offsets = {} } = asset;
        const style = { position: 'absolute', pointerEvents: 'none', zIndex: 20, imageRendering: 'pixelated' };
        if (anchor === 'top-right')         { style.top    = `${offsets.top    ?? 0}px`; style.right  = `${offsets.right  ?? 0}px`; }
        else if (anchor === 'top-left')     { style.top    = `${offsets.top    ?? 0}px`; style.left   = `${offsets.left   ?? 0}px`; }
        else if (anchor === 'top-center')   { style.top    = `${offsets.top    ?? 0}px`; style.left   = '50%'; style.transform = `translateX(calc(-50% + ${offsets.x ?? 0}px))`; }
        else if (anchor === 'bottom-right') { style.bottom = `${offsets.bottom ?? 0}px`; style.right  = `${offsets.right  ?? 0}px`; }
        else if (anchor === 'bottom-left')  { style.bottom = `${offsets.bottom ?? 0}px`; style.left   = `${offsets.left   ?? 0}px`; }
        else if (anchor === 'bottom-center'){ style.bottom = `${offsets.bottom ?? 0}px`; style.left   = '50%'; style.transform = `translateX(calc(-50% + ${offsets.x ?? 0}px))`; }
        return <img key={i} src={image} alt="" draggable={false} style={style} />;
    };

    // ── Header style: mirrors ContentBox headerStyle logic ─────────────────────
    // Default theme: repeat/auto (not cover), same as contentBoxThemes.json "default"
    const headerStyle = useMemo(() => {
        const skinBgImg  = displaySkin?.backgroundImage;
        const skinBgMode = displaySkin?.backgroundMode;
        // Always apply a tint — default maroon if no custom headerBg set
        const tint = sanitiseHex(displayHeaderBg) || '#800000';

        let style = { backgroundColor: tint, overflow: 'hidden' };

        if (skinBgImg) {
            style.backgroundImage = `url(${skinBgImg})`;
            // Mirrors ContentBox default theme: repeat tiles at natural size
            style.backgroundRepeat = 'repeat';
            style.backgroundSize   = 'auto';
            style.backgroundPosition = 'left top';
            if (skinBgMode !== 'overlay') {
                // multiply-blend the background image with the tint colour
                style.backgroundBlendMode = 'multiply';
            }
            // overlay mode: image tiles over the tint with no blending — no extra props needed
        }

        return style;
    }, [displaySkin, displayHeaderBg]);

    // ── Skin border CSS (mirrors EditableBox style tag exactly) ───────────────
    // Uses ContentBox's drop shadow (0 3px 0 0) not Win95's (2px 2px 0 0),
    // and adds the header separator line matching ${sel} > div.flex in EditableBox.
    const skinBorderCss = useMemo(() => {
        const b = displaySkin?.border;
        const r = displaySkin?.borderRadius;
        const radiusCss = r ? ` border-radius: ${r} !important; overflow: hidden;` : '';
        const sel = '.frankly-mail-window';
        let css = '';
        if (b && typeof b === 'object') {
            const outerRing       = b.outer ? `, 0 0 0 2px ${b.outer}` : '';
            const outerHeaderRing = b.outer ? `, 0 2px 0 0 ${b.outer}` : '';
            css = `${sel} { border-color: ${b.inner} !important; box-shadow: 0 3px 0 0 rgba(0,0,0,0.5), 0 0 0 1px ${b.middle}${outerRing} !important;${radiusCss} }`
                + ` ${sel} > div.flex { border-bottom: 1px solid ${b.inner}; box-shadow: 0 1px 0 0 ${b.middle}${outerHeaderRing}; }`;
        } else if (b && typeof b === 'string') {
            css = `${sel} { border-color: ${b} !important;${radiusCss} }`
                + ` ${sel} > div.flex { border-bottom: 1px solid ${b}; }`;
        } else if (r) {
            css = `${sel} {${radiusCss} }`;
        }
        return css;
    }, [displaySkin]);

    // ── Message handlers ───────────────────────────────────────────────────────
    const inboxMessages = messages.filter(m => !m.isDeleted);
    const deletedMessages = messages.filter(m => m.isDeleted);
    const getCurrentMessages = () => activeView === 'inbox' ? inboxMessages : deletedMessages;

    const handleMessageClick = async (message) => {
        setSelectedMessage(message);
        if (!message.isRead) {
            try {
                const response = await markMessageAsRead(message.id);
                if (response.success) {
                    setMessages(messages.map(m => m.id === message.id ? { ...m, isRead: true } : m));
                    setSelectedMessage({ ...message, isRead: true });
                }
            } catch (error) { console.error(error); }
        }
    };

    const handleMarkAllRead = async () => {
        const unreadInView = getCurrentMessages().filter(m => !m.isRead);
        if (unreadInView.length === 0) return;
        try {
            await Promise.all(unreadInView.map(m => markMessageAsRead(m.id)));
            setMessages(messages.map(m => unreadInView.find(um => um.id === m.id) ? { ...m, isRead: true } : m));
            toast.success('All messages marked as read');
        } catch { toast.error('Failed to mark all as read'); }
    };

    const handleCheck = (e, id) => {
        e.stopPropagation();
        const next = new Set(checkedIds);
        next.has(id) ? next.delete(id) : next.add(id);
        setCheckedIds(next);
    };

    const handleSelectAll  = () => setCheckedIds(new Set(getCurrentMessages().map(m => m.id)));
    const handleSelectNone = () => setCheckedIds(new Set());

    const handleBulkDelete = async () => {
        if (checkedIds.size === 0) return;
        try {
            await Promise.all(Array.from(checkedIds).map(id => deleteMessage(id)));
            toast.success('Selected messages deleted');
            setCheckedIds(new Set());
            if (selectedMessage && checkedIds.has(selectedMessage.id)) setSelectedMessage(null);
            loadMessages();
        } catch { toast.error('Failed to delete some messages'); }
    };

    const handleSingleDelete = async () => {
        if (!selectedMessage) return;
        try {
            const response = await deleteMessage(selectedMessage.id);
            if (response.success) { toast.success('Message deleted'); setSelectedMessage(null); loadMessages(); }
            else toast.error('Failed to delete message');
        } catch { toast.error('Failed to delete message'); }
    };

    const formatDate = (dateString) => new Date(dateString).toLocaleString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    }).replace(',', '');

    const decodeEntities = (str) => {
        if (!str) return str;
        const txt = document.createElement('textarea');
        txt.innerHTML = String(str);
        return txt.value;
    };

    const parseFriendRequest    = (msg) => { const c = msg?.content; if (!c || !String(c).startsWith('__PROFILE_FRIEND_REQUEST__')) return null; try { return JSON.parse(String(c).slice('__PROFILE_FRIEND_REQUEST__'.length)); } catch { return null; } };
    const parseProfileReply     = (msg) => { const c = msg?.content; if (!c || !String(c).startsWith('__PROFILE_REPLY__')) return null; try { return JSON.parse(String(c).slice('__PROFILE_REPLY__'.length)); } catch { return null; } };
    const parseProfileAchievement = (msg) => { const c = msg?.content; if (!c || !String(c).startsWith('__PROFILE_ACHIEVEMENT__')) return null; try { return JSON.parse(String(c).slice('__PROFILE_ACHIEVEMENT__'.length)); } catch { return null; } };
    const parseFriendAccepted   = (msg) => { const c = msg?.content; if (!c || !String(c).startsWith('__PROFILE_FRIEND_ACCEPTED__')) return null; try { return JSON.parse(String(c).slice('__PROFILE_FRIEND_ACCEPTED__'.length)); } catch { return null; } };

    const respondFriendRequest = async (fromHabboName, action) => {
        if (responding) return;
        try {
            setResponding(true);
            const data = await respondProfileFriendRequest(fromHabboName, action);
            if (!data?.success) { toast.error(data?.message || 'Failed to respond'); return; }
            toast.success(action === 'accept' ? 'Friend request accepted' : 'Friend request denied');
            await handleSingleDelete();
        } catch { toast.error('Failed to respond'); }
        finally { setResponding(false); }
    };

    const friendRequest      = parseFriendRequest(selectedMessage);
    const friendAccepted     = parseFriendAccepted(selectedMessage);
    const profileReply       = parseProfileReply(selectedMessage);
    const profileAchievement = parseProfileAchievement(selectedMessage);
    const newCount = inboxMessages.filter(m => !m.isRead).length;

    const retroBtnClass    = "bg-[#c0c0c0] text-black border-t-2 border-l-2 border-white border-b-2 border-r-2 border-[#808080] active:border-t-2 active:border-l-2 active:border-[#808080] active:border-b-2 active:border-r-2 active:border-white px-2 py-1 md:px-3 text-xs md:text-sm font-sans focus:outline-none flex items-center justify-center gap-1 md:gap-2 cursor-pointer disabled:opacity-50";
    const retroWindowClass = "bg-[#c0c0c0] border-t-2 border-l-2 border-white border-b-2 border-r-2 border-[#808080] shadow-[2px_2px_0px_0px_rgba(0,0,0,0.5)] font-sans text-black flex flex-col frankly-mail-window";
    const retroSunkenClass = "bg-white border-t-2 border-l-2 border-[#808080] border-b-2 border-r-2 border-white";

    // Page background: if it's a data URL it's a tiled scene image, otherwise a colour
    const pageBgStyle = (() => {
        const bg = sanitiseHex(displayPageBg);
        const dataUrl = !bg && displayPageBg?.startsWith('data:') ? displayPageBg : null;
        if (dataUrl) return { backgroundImage: `url(${dataUrl})`, backgroundRepeat: 'repeat', backgroundSize: 'auto' };
        if (bg)      return { backgroundColor: bg };
        return {};
    })();

    if (loading) {
        return (
            <div className="w-full min-h-screen p-4 flex items-start justify-center">
                <div className="bg-[#c0c0c0] border-t-2 border-l-2 border-white border-b-2 border-r-2 border-[#808080] shadow-[2px_2px_0px_0px_rgba(0,0,0,0.5)] font-sans text-black flex flex-col p-1 w-64">
                    <div className="bg-maroon text-white px-2 py-1 font-bold text-sm">System Message</div>
                    <div className="p-4 text-center">Loading FranklyMail...</div>
                </div>
            </div>
        );
    }

    const selectedTiledScene = tiledScenes.find(s => s.id === selectedBgSceneId) || null;

    return (
        <div className="w-full min-h-screen p-2 md:p-8 flex flex-col items-center" style={pageBgStyle}>
            <style>{retroScrollbarStyles}{skinBorderCss}</style>

            {/*
              Outer relative wrapper — skin assets are positioned absolutely here,
              exactly as EditableBox wraps ContentBox in a div.relative
            */}
            <div className="relative w-full max-w-6xl">
                <div className={retroWindowClass}>

                    {/* Title bar — matches ContentBox header: px-4 on outer, py-2 on inner content */}
                    <div className="text-white px-4 flex items-center select-none font-bold text-sm" style={headerStyle}>
                        <div className="flex items-center gap-2 py-2">
                            <span>✉️</span>
                            <span>FranklyMail - {userData?.habboName || userData?.username || 'User'}</span>
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row p-2 gap-4">
                        <div className="flex-1 flex flex-col gap-2">
                            <div className="flex px-1 gap-1 border-b border-white relative top-[2px] z-10">
                                <button
                                    onClick={() => { setActiveView('inbox'); setCheckedIds(new Set()); setSelectedMessage(null); }}
                                    className={`px-3 py-1 text-xs md:text-sm border-t-2 border-l-2 border-r-2 rounded-t-sm focus:outline-none ${activeView === 'inbox' ? 'bg-[#c0c0c0] border-white border-r-[#808080] pb-2 -mb-[2px] z-20 font-bold' : 'bg-[#a0a0a0] border-white border-r-[#808080] mt-[2px] cursor-pointer'}`}
                                >
                                    Inbox {newCount > 0 && `(${newCount})`}
                                </button>
                                <button
                                    onClick={() => { setActiveView('deleted'); setCheckedIds(new Set()); setSelectedMessage(null); }}
                                    className={`px-3 py-1 text-xs md:text-sm border-t-2 border-l-2 border-r-2 rounded-t-sm focus:outline-none ${activeView === 'deleted' ? 'bg-[#c0c0c0] border-white border-r-[#808080] pb-2 -mb-[2px] z-20 font-bold' : 'bg-[#a0a0a0] border-white border-r-[#808080] mt-[2px] cursor-pointer'}`}
                                >
                                    Trash
                                </button>
                            </div>

                            <div className="border-t-2 border-white flex flex-col">
                                <div className="flex flex-wrap gap-2 py-2 px-1 bg-[#c0c0c0] items-center">
                                    <button onClick={handleSelectAll}  className={retroBtnClass} disabled={activeView === 'deleted'}>Select All</button>
                                    <button onClick={handleSelectNone} className={retroBtnClass} disabled={activeView === 'deleted'}>Deselect all</button>
                                    <button onClick={handleMarkAllRead} className={retroBtnClass} disabled={activeView === 'deleted'}>Mark all as Read</button>
                                    <button onClick={handleBulkDelete} className={retroBtnClass} disabled={activeView === 'deleted' || checkedIds.size === 0}>
                                        🗑️ Delete ({checkedIds.size})
                                    </button>
                                    <button onClick={handleOpenInboxSettings} className={`${retroBtnClass} ml-auto`}>
                                        Inbox Settings
                                    </button>
                                </div>

                                <div className={`${retroSunkenClass} h-[250px] md:h-[300px] overflow-y-scroll scrollbar-retro`}>
                                    {getCurrentMessages().length === 0 ? (
                                        <div className="p-10 text-center text-gray-500 italic text-sm">Folder is empty.</div>
                                    ) : (
                                        <table className="w-full text-xs md:text-sm text-left border-collapse table-fixed">
                                            <thead className="bg-[#c0c0c0] sticky top-0 border-b-2 border-[#808080] z-30">
                                            <tr>
                                                <th className="border-r border-b border-white p-1 w-8 text-center font-normal">✔</th>
                                                <th className="border-r border-b border-white p-1 w-10 text-center font-normal">!</th>
                                                <th className="border-r border-b border-white p-1 w-28 md:w-40 font-normal">Date</th>
                                                <th className="border-b border-white p-1 font-normal">Subject</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {getCurrentMessages().map((message) => (
                                                <tr
                                                    key={message.id}
                                                    onClick={() => handleMessageClick(message)}
                                                    className={`cursor-pointer hover:bg-maroon hover:text-white ${selectedMessage?.id === message.id ? 'bg-maroon text-white' : 'even:bg-[#f9f9f9]'} ${!message.isRead && activeView === 'inbox' ? 'font-bold' : ''}`}
                                                >
                                                    <td className="p-1 text-center border-r border-gray-200">
                                                        <input
                                                            type="checkbox"
                                                            checked={checkedIds.has(message.id)}
                                                            onChange={(e) => handleCheck(e, message.id)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            disabled={activeView === 'deleted'}
                                                            className={activeView === 'deleted' ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                                                        />
                                                    </td>
                                                    <td className="p-1 text-center border-r border-gray-200">{message.isRead ? '📖' : '✉️'}</td>
                                                    <td className="p-1 border-r border-gray-200 whitespace-nowrap overflow-hidden">{formatDate(message.sentDate)}</td>
                                                    <td className="p-1 truncate">{message.title}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>

                                <div className={`${retroSunkenClass} w-full px-2 py-0.5 text-xs border-t-2 border-t-[#808080]`}>
                                    Inbox: {inboxMessages.length} &nbsp;·&nbsp; Unread: {newCount} &nbsp;·&nbsp; Trash: {deletedMessages.length} &nbsp;·&nbsp; {checkedIds.size > 0 ? `${checkedIds.size} selected` : 'None selected'}
                                </div>

                                <div className="h-2 bg-[#c0c0c0] border-t-2 border-[#808080] border-b-2 border-white my-0.5"></div>

                                <div className={`${retroSunkenClass} h-[400px] flex flex-col bg-[#dfdfdf]`}>
                                    {!selectedMessage ? (
                                        <div className="flex-1 flex items-center justify-center text-gray-500 italic p-4 text-center">
                                            Select a message to read.
                                        </div>
                                    ) : (
                                        <div className="flex flex-col h-full overflow-hidden">
                                            <div className="p-2 border-b border-[#808080] bg-[#c0c0c0] text-xs md:text-sm">
                                                <div className="flex justify-between items-start">
                                                    <div className="flex flex-col gap-0.5">
                                                        <div><span className="text-gray-600 inline-block w-14">From:</span> <strong>Frank & The Frankly Team</strong></div>
                                                        <div><span className="text-gray-600 inline-block w-14">Subject:</span> <strong className="text-maroon">{selectedMessage.title}</strong></div>
                                                    </div>
                                                    <button onClick={handleSingleDelete} className={retroBtnClass} disabled={activeView === 'deleted'}>🗑️ Delete</button>
                                                </div>
                                            </div>
                                            <div className="flex-1 p-4 overflow-y-scroll scrollbar-retro bg-white text-sm leading-relaxed">
                                                {selectedMessage.image && (
                                                    <img src={selectedMessage.image} alt="Attachment" className="float-right ml-4 mb-4 border-2 border-[#808080] p-1 bg-[#c0c0c0] max-w-[150px]" />
                                                )}
                                                {friendRequest && (
                                                    <div style={{ marginBottom: 16, fontFamily: 'sans-serif', fontSize: 13 }}>
                                                        <p style={{ margin: '0 0 10px 0' }}>
                                                            Dear <strong>{userData?.habboName}</strong>,
                                                        </p>
                                                        <p style={{ margin: '0 0 12px 0' }}>
                                                            <strong>{friendRequest.fromHabboName}</strong> has sent you a friend request on Frankly Origins.
                                                        </p>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '2px inset #808080', backgroundColor: '#c0c0c0', padding: '8px 10px', marginBottom: 12 }}>
                                                            <HabboImage habbo={friendRequest.fromHabboName} hotel="COM" size="a" headonly="true" />
                                                            <strong style={{ fontSize: 14 }}>{friendRequest.fromHabboName}</strong>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                                            <button onClick={() => respondFriendRequest(friendRequest.fromHabboName, 'accept')} disabled={responding || activeView === 'deleted'} className={retroBtnClass}>Accept</button>
                                                            <button onClick={() => respondFriendRequest(friendRequest.fromHabboName, 'deny')} disabled={responding || activeView === 'deleted'} className={retroBtnClass}>Deny</button>
                                                        </div>
                                                        <hr style={{ borderTop: '1px solid #c0c0c0', margin: '12px 0' }} />
                                                        <p style={{ margin: 0, fontSize: 11, color: '#808080' }}>
                                                            This message was automatically generated by the Frankly Origins system.
                                                        </p>
                                                    </div>
                                                )}
                                                {friendAccepted && (
                                                    <div style={{ marginBottom: 16, fontFamily: 'sans-serif', fontSize: 13 }}>
                                                        <p style={{ margin: '0 0 10px 0' }}>
                                                            Dear <strong>{userData?.habboName}</strong>,
                                                        </p>
                                                        {friendAccepted.acceptedBy === userData?.habboName ? (
                                                            <p style={{ margin: '0 0 12px 0' }}>
                                                                You accepted a friend request from <strong>{friendAccepted.requestedBy}</strong>. You are now friends!
                                                            </p>
                                                        ) : (
                                                            <p style={{ margin: '0 0 12px 0' }}>
                                                                <strong>{friendAccepted.acceptedBy}</strong> accepted your friend request. You are now friends!
                                                            </p>
                                                        )}
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '2px inset #808080', backgroundColor: '#c0c0c0', padding: '8px 10px', marginBottom: 12 }}>
                                                            <HabboImage habbo={friendAccepted.acceptedBy === userData?.habboName ? friendAccepted.requestedBy : friendAccepted.acceptedBy} hotel="COM" size="a" headonly="true" />
                                                            <strong style={{ fontSize: 14 }}>{friendAccepted.acceptedBy === userData?.habboName ? friendAccepted.requestedBy : friendAccepted.acceptedBy}</strong>
                                                        </div>
                                                        <p style={{ margin: '0 0 12px 0' }}>
                                                            You can visit their <a href={`/members/profile/${friendAccepted.acceptedBy === userData?.habboName ? friendAccepted.requestedBy : friendAccepted.acceptedBy}`} style={{ color: '#800000', fontWeight: 'bold' }}>profile page</a>.
                                                        </p>
                                                        <hr style={{ borderTop: '1px solid #c0c0c0', margin: '12px 0' }} />
                                                        <p style={{ margin: 0, fontSize: 11, color: '#808080' }}>
                                                            This message was automatically generated by the Frankly Origins system.
                                                        </p>
                                                    </div>
                                                )}
                                                {profileReply && (
                                                    <div style={{ marginBottom: 16, fontFamily: 'sans-serif', fontSize: 13 }}>
                                                        <p style={{ margin: '0 0 10px 0' }}>
                                                            Dear <strong>{userData?.habboName}</strong>,
                                                        </p>
                                                        <p style={{ margin: '0 0 12px 0' }}>
                                                            You have received <strong>{profileReply.count > 1 ? `${profileReply.count} new replies` : '1 new reply'}</strong> on your profile post.
                                                        </p>
                                                        {profileReply.contextExtract && (
                                                            <div style={{ borderLeft: '3px solid #800000', paddingLeft: 10, margin: '0 0 12px 0', color: '#555', fontStyle: 'italic' }}>
                                                                "{decodeEntities(profileReply.contextExtract)}"
                                                            </div>
                                                        )}
                                                        <p style={{ margin: '0 0 12px 0' }}>
                                                            You can view the {profileReply.count > 1 ? 'replies' : 'reply'} on your{' '}
                                                            <a href={`/members/profile/${userData?.habboName || userData?.username}`} style={{ color: '#800000', fontWeight: 'bold' }}>profile page</a>.
                                                        </p>
                                                        <hr style={{ borderTop: '1px solid #c0c0c0', margin: '12px 0' }} />
                                                        <p style={{ margin: 0, fontSize: 11, color: '#808080' }}>
                                                            This message was automatically generated by the Frankly Origins system.
                                                        </p>
                                                    </div>
                                                )}
                                                {profileAchievement && (
                                                    <div style={{ marginBottom: 16, fontFamily: 'sans-serif', fontSize: 13 }}>
                                                        <p style={{ margin: '0 0 10px 0' }}>
                                                            Dear <strong>{userData?.habboName}</strong>,
                                                        </p>
                                                        <p style={{ margin: '0 0 12px 0' }}>
                                                            Congratulations! You have been awarded a new achievement:
                                                        </p>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, border: '2px inset #808080', backgroundColor: '#c0c0c0', padding: '8px 10px', marginBottom: 12 }}>
                                                            <img
                                                                src={getBadgeUrl(profileAchievement.badgeID, profileAchievement.uploaded)}
                                                                alt={profileAchievement.badgeID}
                                                                style={{ imageRendering: 'pixelated' }}
                                                            />
                                                            <div>
                                                                <strong style={{ fontSize: 15, display: 'block' }}>{profileAchievement.achievementName}</strong>
                                                                {profileAchievement.achievementDescription && (
                                                                    <span style={{ fontSize: 12, color: '#555' }}>{profileAchievement.achievementDescription}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <p style={{ margin: '0 0 12px 0' }}>
                                                            You can view your achievements on your{' '}
                                                            <a href={`/members/profile/${userData?.habboName || userData?.username}`} style={{ color: '#800000', fontWeight: 'bold' }}>profile page</a>.
                                                        </p>
                                                        <hr style={{ borderTop: '1px solid #c0c0c0', margin: '12px 0' }} />
                                                        <p style={{ margin: 0, fontSize: 11, color: '#808080' }}>
                                                            This message was automatically generated by the Frankly Origins system.
                                                        </p>
                                                    </div>
                                                )}
                                                {!friendRequest && !friendAccepted && !profileReply && !profileAchievement && (
                                                    <div style={{ fontFamily: 'sans-serif', fontSize: 13 }}>
                                                        <p style={{ margin: '0 0 10px 0' }}>
                                                            Dear <strong>{userData?.habboName}</strong>,
                                                        </p>
                                                        <div className="whitespace-pre-wrap break-words" style={{ margin: '0 0 12px 0' }}>
                                                            {selectedMessage.content}
                                                        </div>
                                                        <hr style={{ borderTop: '1px solid #c0c0c0', margin: '12px 0' }} />
                                                        <p style={{ margin: 0, fontSize: 11, color: '#808080' }}>
                                                            This message was automatically generated by the Frankly Origins system.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="hidden md:flex min-w-[180px] flex-shrink-0">
                            <div className={`${retroSunkenClass} p-2 h-full flex flex-col items-center bg-[#c0c0c0]`}>
                                <div className="w-full bg-[#808080] text-white text-[10px] px-1 mb-2 font-bold uppercase">Advertisement</div>
                                <div className="border-2 border-[#808080] shadow-sm bg-white p-1">
                                    <img
                                        src="/img/staysafe.png"
                                        alt="Stay Safe Online"
                                        style={{ width: 160, height: 600, imageRendering: 'pixelated', display: 'block' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Skin assets — outside the window div, positioned relative to outer wrapper (mirrors EditableBox) */}
                {getBoxAssets(displaySkin).map((asset, i) => renderSkinAsset(asset, i))}
            </div>

            {/* ── Inbox Settings Modal ────────────────────────────────────────── */}
            {showInboxSettingsModal && (
                <div
                    className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-3 sm:p-4"
                    onMouseDown={(e) => { if (e.target === e.currentTarget) handleCancelSettings(); }}
                >
                    <div className="bg-[#c0c0c0] border-t-2 border-l-2 border-white border-b-2 border-r-2 border-[#808080] shadow-[2px_2px_0px_0px_rgba(0,0,0,0.5)] font-sans text-black flex flex-col w-full max-w-sm max-h-[90vh]">
                        <div className="bg-maroon text-white px-2 py-1 flex justify-between items-center select-none font-bold text-sm shrink-0">
                            <span>Inbox Settings</span>
                            <button onClick={handleCancelSettings} className="hover:bg-white/20 px-1 leading-none text-lg focus:outline-none">✕</button>
                        </div>

                        <div className="p-3 flex flex-col gap-3 overflow-y-auto">
                            {/* Page Background */}
                            <div className={`${retroSunkenClass} p-2 flex flex-col gap-2`}>
                                <div className="text-xs font-bold uppercase text-gray-600">Page Background</div>
                                <div className="flex items-center gap-2">
                                    <div
                                        className="w-8 h-8 border-2 border-[#808080] shrink-0"
                                        style={(() => {
                                            const bg = sanitiseHex(draftPageBg);
                                            const dataUrl = !bg && draftPageBg?.startsWith('data:') ? draftPageBg : null;
                                            if (dataUrl) return { backgroundImage: `url(${dataUrl})`, backgroundSize: 'cover' };
                                            if (bg) return { backgroundColor: bg };
                                            return { backgroundColor: '#c0c0c0' };
                                        })()}
                                    />
                                    <span className="text-xs font-mono text-gray-600 flex-1 truncate">
                                        {sanitiseHex(draftPageBg) ? sanitiseHex(draftPageBg) : (draftPageBg?.startsWith('data:') ? 'Tiled scene' : 'Default')}
                                    </span>
                                    <button onClick={handleOpenPageBgModal} className={retroBtnClass}>Change...</button>
                                </div>
                            </div>

                            {/* Header Colour — inline ColorPicker, no sub-modal */}
                            <div className={`${retroSunkenClass} p-2 flex flex-col gap-2`}>
                                <div className="text-xs font-bold uppercase text-gray-600">Header Colour</div>
                                <div className="flex items-center gap-2">
                                    <ColorPicker
                                        value={sanitiseHex(draftHeaderBg) || '#800000'}
                                        onChange={(e) => setDraftHeaderBg(e.target.value)}
                                        className="w-8 h-8 shrink-0"
                                        title="Header colour"
                                    />
                                    <span className="text-xs font-mono text-gray-600 flex-1">
                                        {sanitiseHex(draftHeaderBg) || 'Default (Maroon)'}
                                    </span>
                                    {sanitiseHex(draftHeaderBg) && (
                                        <button onClick={() => setDraftHeaderBg(null)} className={retroBtnClass}>Clear</button>
                                    )}
                                </div>
                            </div>

                            {/* Skin */}
                            <div className={`${retroSunkenClass} p-2 flex flex-col gap-2`}>
                                <div className="text-xs font-bold uppercase text-gray-600">Skin</div>
                                <div className="flex items-center gap-2">
                                    {(() => {
                                        const ds = SKINS.find(s => s.id === draftSkinId && s.id !== 'default') ?? null;
                                        return (
                                            <div
                                                className="w-8 h-8 border border-[#808080] overflow-hidden relative shrink-0"
                                                style={{
                                                    ...(ds?.backgroundImage && ds.backgroundMode !== 'overlay' ? { backgroundImage: `url(${ds.backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
                                                    backgroundColor: !ds ? '#c0c0c0' : (ds.backgroundMode === 'overlay' ? '#e5e7eb' : undefined),
                                                }}
                                            >
                                                {ds?.backgroundMode === 'overlay' && ds.backgroundImage && (
                                                    <img src={ds.backgroundImage} alt="" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', imageRendering: 'pixelated', maxWidth: 'none' }} />
                                                )}
                                                {getBoxAssets(ds)[0] && (
                                                    <img src={getBoxAssets(ds)[0].image} alt="" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', imageRendering: 'pixelated', maxWidth: 'none' }} />
                                                )}
                                            </div>
                                        );
                                    })()}
                                    <span className="text-xs flex-1 truncate">
                                        {SKINS.find(s => s.id === draftSkinId)?.name || 'Default'}
                                    </span>
                                    <button ref={skinBtnRef} onClick={openSkinPicker} className={retroBtnClass}>Change...</button>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between gap-2 pt-1">
                                <button onClick={handleResetInboxSettings} className={retroBtnClass}>Reset to Default</button>
                                <div className="flex gap-2">
                                    <button onClick={handleCancelSettings} className={retroBtnClass}>Cancel</button>
                                    <button onClick={handleSaveInboxSettings} className={`${retroBtnClass} font-bold`}>Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Skin picker popover (mirrors EditableBox) ───────────────────── */}
            {skinPickerOpen && (
                <div
                    ref={skinPickerRef}
                    style={{ position: 'fixed', top: skinPickerPos.top, bottom: skinPickerPos.bottom, right: skinPickerPos.right, zIndex: 9999 }}
                    className="bg-offwhite border border-gray-300 shadow-[0_3px_0_0_rgba(0,0,0,0.5)]"
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="p-2 grid grid-cols-5 content-start gap-2 h-[200px] w-[260px] overflow-y-scroll habbo-scroll-v">
                        {SKINS.map(skin => {
                            const saveable = isSkinSaveable(skin.id, isStaff, ownedSkinIds);
                            return (
                                <button
                                    key={skin.id}
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setDraftSkinId(skin.id); setSkinPickerOpen(false); }}
                                    className={`flex flex-col max-h-16 items-center gap-1 p-1 hover:bg-black/5 transition-colors ${draftSkinId === skin.id ? 'outline outline-2 outline-coal' : ''}`}
                                    title={!saveable ? `${skin.name} (preview – you don't own this skin yet)` : skin.name}
                                >
                                    <div
                                        className="w-10 h-10 border border-gray-300 overflow-hidden relative shrink-0"
                                        style={{
                                            ...(skin.backgroundImage && skin.backgroundMode !== 'overlay' ? { backgroundImage: `url(${skin.backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
                                            backgroundColor: skin.backgroundMode === 'overlay' ? '#e5e7eb' : (!skin.backgroundImage ? '#e5e7eb' : undefined),
                                        }}
                                    >
                                        {skin.backgroundMode === 'overlay' && skin.backgroundImage && (
                                            <img src={skin.backgroundImage} alt="" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', imageRendering: 'pixelated', maxWidth: 'none' }} />
                                        )}
                                        {getBoxAssets(skin)[0] && (
                                            <img src={getBoxAssets(skin)[0].image} alt="" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', imageRendering: 'pixelated', maxWidth: 'none' }} />
                                        )}
                                        {!saveable && (
                                            <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', pointerEvents: 'none' }}>
                                                <span style={{ transform: 'rotate(-35deg)', fontSize: '14px', fontFamily: "'Jersey 10', sans-serif", fontWeight: 900, color: 'rgba(0,0,0,0.82)', textTransform: 'uppercase', whiteSpace: 'nowrap', lineHeight: 1, userSelect: 'none' }}>PREVIEW</span>
                                            </div>
                                        )}
                                    </div>
                                    <span className="text-[10px] text-coal font-['Jersey_10'] uppercase leading-none tracking-wide">{skin.name}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Page Background Modal (mirrors Profile.jsx showBgModal) ─────── */}
            {showPageBgModal && (
                <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-4">
                    <div className="bg-offwhite border-t-2 sm:border-2 border-black shadow-xl flex flex-col w-full sm:w-auto" style={{ maxHeight: '90vh', width: 'min(860px, 100vw)' }}>
                        <div className="px-4 py-3 bg-coal text-offwhite font-['Jersey_10'] text-2xl uppercase flex justify-between items-center shrink-0">
                            <span>Select Background</span>
                            <button onClick={() => setShowPageBgModal(false)} className="hover:text-gold text-3xl leading-none">&times;</button>
                        </div>

                        <div className="flex flex-col sm:flex-row flex-1 min-h-0 overflow-hidden" style={{ height: 'clamp(300px, 60vh, 600px)' }}>
                            {/* Options — horizontal scroll on mobile, vertical sidebar on desktop */}
                            <div className="shrink-0 flex flex-col sm:w-52 bg-black/5 border-b sm:border-b-0 sm:border-r border-black/10">
                                <div className="hidden sm:block px-3 pt-3 pb-1 text-xs font-['Jersey_10'] uppercase text-coal/50 tracking-widest">Options</div>
                                <div className="flex sm:flex-col overflow-x-auto sm:overflow-x-hidden overflow-y-hidden sm:overflow-y-auto habbo-scroll-v p-2 gap-1">
                                    <button
                                        onClick={() => setBgModalMode('color')}
                                        className={['px-3 py-2 text-left font-[\'Jersey_10\'] text-sm uppercase border transition-none whitespace-nowrap sm:whitespace-normal sm:truncate shrink-0 sm:shrink', bgModalMode === 'color' ? 'bg-gold border-black text-coal' : 'bg-offwhite border-gray-300 hover:bg-gray-100 text-coal'].join(' ')}
                                    >
                                        Background Colour
                                    </button>
                                    {isFetchingTiledScenes ? (
                                        <div className="text-center py-2 px-3 font-['Jersey_10'] text-coal/50 animate-pulse text-sm whitespace-nowrap shrink-0">Loading…</div>
                                    ) : tiledScenes.length === 0 ? (
                                        <div className="font-['Jersey_10'] text-xs text-coal/40 text-center py-2 px-3 uppercase whitespace-nowrap sm:whitespace-normal shrink-0">No tiled scenes</div>
                                    ) : tiledScenes.map(scene => (
                                        <button
                                            key={scene.id}
                                            onClick={() => { setBgModalMode('scene'); setSelectedBgSceneId(scene.id); }}
                                            title={scene.imageName || '(Untitled)'}
                                            className={['px-3 py-2 text-left font-[\'Jersey_10\'] text-sm uppercase border transition-none whitespace-nowrap sm:whitespace-normal sm:truncate shrink-0 sm:shrink', bgModalMode === 'scene' && selectedBgSceneId === scene.id ? 'bg-gold border-black text-coal' : 'bg-offwhite border-gray-300 hover:bg-gray-100 text-coal'].join(' ')}
                                        >
                                            {scene.imageName || '(Untitled)'}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Right: preview / picker */}
                            <div className="flex-1 flex flex-col min-w-0 min-h-0">
                                {bgModalMode === 'color' ? (
                                    <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6">
                                        <div className="font-['Jersey_10'] uppercase text-sm text-coal/60">Page Background Colour</div>
                                        <div className="relative" style={{ width: 80, height: 80 }}>
                                            <ColorPicker
                                                value={bgPickerDraftColor}
                                                onChange={(e) => setBgPickerDraftColor(e.target.value)}
                                                className="absolute inset-0 w-full h-full border-2 border-black cursor-pointer"
                                                style={{ backgroundColor: bgPickerDraftColor }}
                                            />
                                        </div>
                                        <div className="font-['Jersey_10'] text-xs text-coal/50">{bgPickerDraftColor}</div>
                                    </div>
                                ) : (
                                    <div className="flex-1 bg-coal_dark flex items-center justify-center overflow-hidden min-h-0">
                                        {selectedBgSceneId && tiledScenePreviews.get(selectedBgSceneId) ? (
                                            <img src={tiledScenePreviews.get(selectedBgSceneId)} alt="Scene preview" className="max-w-full max-h-full block" style={{ imageRendering: 'pixelated' }} />
                                        ) : selectedBgSceneId ? (
                                            <div className="font-['Jersey_10'] text-xl text-white/40 animate-pulse">Generating preview…</div>
                                        ) : (
                                            <div className="font-['Jersey_10'] text-xl text-white/25">Select an option</div>
                                        )}
                                    </div>
                                )}

                                <div className="px-4 py-3 bg-offwhite border-t border-black/10 flex items-center justify-between gap-4 shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => { setDraftPageBg(null); setSelectedBgSceneId(null); setShowPageBgModal(false); }}
                                        className="px-4 py-1.5 bg-offwhite text-coal font-['Jersey_10'] text-base uppercase border border-black/40 hover:bg-black/5 shrink-0"
                                    >Reset to Default</button>
                                    <div className="font-['Jersey_10'] text-sm text-coal/50 uppercase truncate hidden sm:block">
                                        {bgModalMode === 'color' ? 'Colour selected' : (selectedTiledScene?.imageName || (selectedBgSceneId ? '(Untitled)' : 'No option selected'))}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleConfirmPageBgModal}
                                        disabled={bgModalMode === 'scene' && (!selectedBgSceneId || !tiledScenePreviews.get(selectedBgSceneId))}
                                        className="px-6 py-1.5 bg-gold text-coal font-['Jersey_10'] text-xl uppercase border border-black disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                                        style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.4)' }}
                                    >Confirm</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Messages;
